import React, { useEffect, useState } from 'react';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import { getPreviousDistinctCargoContentsInTank } from 'src/utils/stowage/manifest/cargoManifestUtils';
import { useStowageStore } from 'src/context/StowageContext';
import { useRotationStore } from 'src/context/RotationContext';
import { getBerthCallForSelection } from 'src/utils/stowage/stowageRotationUtils';
import { CargoContent } from 'src/types/ViewModel/Stowage/Manifest/CargoContent';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import CargoNumber from '../common/cargoNumber';
import { getFosfaListStatus } from 'src/utils/stowage/productUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import OdfjellPanel, { PANEL_CONSTANTS } from 'src/components/ui/odfjellPanel/OdfjellPanel';
import { OverlayMenuState } from 'src/types/ViewModel/Vessel/TankPlan';
import { CargoListItem, IndexNumber, TradeName, CommodityBookLink, FosfaStatus } from './TankPreviousCargoesContainer.styled';

interface TankPreviousCargoesProps {
    tank: Tank;
}

const renderTankPreviousCargoes = (previousCargoContents: CargoContent[]) => {
    return previousCargoContents?.length > 0 ? (
        <ul>
            {previousCargoContents.map((cc, index) => (
                <CargoListItem key={`tank_previous_cargo_${index}`} className="tank-previous-cargo">
                    <IndexNumber>{index + 1}</IndexNumber>
                    <CargoNumber includeColoredBackground={true} includeHash={true} cargo={cc.cargo} />
                    <TradeName>{cc.cargo.product.tradeName}</TradeName>
                    <CommodityBookLink
                        href={'https://commoditybook.odfjell.com/#/product/' + cc.cargo.productId}
                        target="_blank"
                        title="Click here to open the Commodity Book"
                        rel="noreferrer"
                    >
                        <FontAwesomeIcon className="external-link-icon" icon="external-link-alt" />
                    </CommodityBookLink>
                    <FosfaStatus>{getFosfaListStatus(cc.cargo.product)}</FosfaStatus>
                </CargoListItem>
            ))}
        </ul>
    ) : (
        <div>No previous cargoes</div>
    );
};

const TankPreviousCargoesContainer: React.FC<TankPreviousCargoesProps> = ({ tank }) => {
    const { activeStowagePlan, setOverlayMenuState } = useStowageStore();
    const { selectedStowageRotationPortCall, selectedStowageRotationBerthCall } = useRotationStore();
    const [previousCargoContents, setPreviousCargoContents] = useState<CargoContent[]>([]);
    let berthCall: RotationBerthCall = null;

    useEffect(() => {
        if (activeStowagePlan?.manifest && tank && selectedStowageRotationPortCall) {
            berthCall = getBerthCallForSelection(activeStowagePlan, selectedStowageRotationPortCall, selectedStowageRotationBerthCall);
            if (berthCall) {
                setPreviousCargoContents(getPreviousDistinctCargoContentsInTank(activeStowagePlan, tank, berthCall, 3));
            } else {
                setPreviousCargoContents([]);
            }
        }
    }, [activeStowagePlan, tank, selectedStowageRotationPortCall, selectedStowageRotationBerthCall]);

    const onClosePanel = () => {
        setOverlayMenuState({ visible: false } as OverlayMenuState);
    };

    return (
        activeStowagePlan?.manifest && (
            <OdfjellPanel
                headerTitle={'Previous cargoes in tank ' + tank.odfjellTankName}
                defaultPosition="center"
                panelWidth={PANEL_CONSTANTS.LARGE_WIDTH + 50}
                onCancel={onClosePanel}
                onAction={onClosePanel}
                actionButtonText="Close"
            >
                <div className="tank-previous-cargoes-content">{tank && renderTankPreviousCargoes(previousCargoContents)}</div>
            </OdfjellPanel>
        )
    );
};

export default TankPreviousCargoesContainer;
